import { Facebook, Globe, Instagram, MoreHorizontal } from "lucide-react";
import { api, type CarouselItem } from "@/lib/api";
import { composeCaptionPreview } from "@/lib/carouselCaptionCompose";
import { cn } from "@/lib/utils";

type FbMode = "gallery" | "slide1" | "reel";

function captionCorpus(item: CarouselItem): string {
  return String(item.caption || item.draft_text || "").trim();
}

function isCzasotekst(item: CarouselItem): boolean {
  return (
    item.kind === "czasotekst" ||
    (item as { content?: { kind?: string } }).content?.kind === "czasotekst"
  );
}

function slideSrc(item: CarouselItem, indexN: number, bust: string | number): string {
  return `${api.carouselSlidePng(item.id, indexN)}?v=${bust}`;
}

/** FB desktop: 1 / 2 / 3 / 4 / 5+ (2 na górze, 3 na dole, +N na ostatniej widocznej). */
function FbGalleryMosaic({
  item,
  bust,
}: {
  item: CarouselItem;
  bust: string | number;
}) {
  const slides = item.slides || [];
  const n = slides.length;
  if (!n) {
    return (
      <div className="flex aspect-square w-full items-center justify-center bg-muted px-3 text-center text-xs text-muted-foreground">
        Brak media do podglądu. Złóż PNG w Wyjściu.
      </div>
    );
  }

  const cell = (slide: (typeof slides)[0], opts?: { overlayPlus?: number; className?: string }) => (
    <div key={slide.id || slide.index_n} className={cn("relative min-h-0 min-w-0 overflow-hidden bg-neutral-200", opts?.className)}>
      <img
        src={slideSrc(item, slide.index_n, bust)}
        alt={`slajd ${slide.index_n}`}
        className="h-full w-full object-cover"
      />
      {opts?.overlayPlus && opts.overlayPlus > 0 ? (
        <div className="absolute inset-0 flex items-center justify-center bg-black/45">
          <span className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">
            +{opts.overlayPlus}
          </span>
        </div>
      ) : null}
    </div>
  );

  if (n === 1) {
    return <div className="aspect-[4/5] w-full overflow-hidden bg-neutral-200">{cell(slides[0])}</div>;
  }

  if (n === 2) {
    return (
      <div className="grid aspect-[2/1] w-full grid-cols-2 gap-[2px] bg-white">
        {slides.slice(0, 2).map((s) => cell(s))}
      </div>
    );
  }

  if (n === 3) {
    // FB: duży lewy + dwa stacked po prawej.
    return (
      <div className="grid aspect-square w-full grid-cols-2 grid-rows-2 gap-[2px] bg-white">
        {cell(slides[0], { className: "row-span-2" })}
        {cell(slides[1])}
        {cell(slides[2])}
      </div>
    );
  }

  if (n === 4) {
    return (
      <div className="grid aspect-square w-full grid-cols-2 grid-rows-2 gap-[2px] bg-white">
        {slides.slice(0, 4).map((s) => cell(s))}
      </div>
    );
  }

  // 5+: dwa na górze, trzy na dole; na 5. kafelku + (n - 5).
  const plus = Math.max(0, n - 5);
  const visible = slides.slice(0, 5);
  return (
    <div className="flex aspect-square w-full flex-col gap-[2px] bg-white">
      <div className="grid min-h-0 flex-[1.15] grid-cols-2 gap-[2px]">
        {visible.slice(0, 2).map((s) => cell(s))}
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-3 gap-[2px]">
        {visible.slice(2, 5).map((s, i) =>
          cell(s, i === 2 && plus > 0 ? { overlayPlus: plus } : undefined),
        )}
      </div>
    </div>
  );
}

function IgGalleryStrip({
  item,
  bust,
}: {
  item: CarouselItem;
  bust: string | number;
}) {
  const slides = item.slides || [];
  if (!slides.length) {
    return (
      <div className="flex aspect-[4/5] w-full items-center justify-center bg-muted px-3 text-center text-xs text-muted-foreground">
        Brak media do podglądu. Złóż rolkę / PNG w Wyjściu.
      </div>
    );
  }
  return (
    <div className="w-full overflow-x-auto bg-muted">
      <div className="flex w-max gap-2 p-2">
        {slides.map((slide) => (
          <div
            key={slide.id || slide.index_n}
            className="aspect-[4/5] w-40 shrink-0 overflow-hidden rounded-sm bg-black/5 sm:w-48"
          >
            <img
              src={slideSrc(item, slide.index_n, bust)}
              alt={`slajd ${slide.index_n}`}
              className="h-full w-full object-cover"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function MediaBlock({
  item,
  mode,
  bust,
  platform,
}: {
  item: CarouselItem;
  mode: "reel" | "gallery" | "slide1";
  bust: string | number;
  platform: "ig" | "fb";
}) {
  const slides = item.slides || [];
  if (mode === "reel" && item.has_reel) {
    const czasotekst = isCzasotekst(item);
    return (
      <div
        className={cn(
          "relative w-full overflow-hidden bg-black",
          czasotekst ? "mx-auto flex max-h-[300px] justify-center" : "aspect-[9/16]",
        )}
      >
        <video
          className={cn(
            "object-contain",
            czasotekst ? "max-h-[300px] w-auto max-w-full" : "h-full w-full",
          )}
          src={`${api.carouselReelUrl(item.id)}?v=${bust}`}
          controls
          loop
          playsInline
          muted={false}
        />
      </div>
    );
  }
  if (!slides.length) {
    return (
      <div className="flex aspect-[4/5] w-full items-center justify-center bg-muted px-3 text-center text-xs text-muted-foreground">
        Brak media do podglądu. Złóż rolkę / PNG w Wyjściu.
      </div>
    );
  }
  if (mode === "slide1") {
    const slide = slides[0];
    return (
      <div className={cn("w-full overflow-hidden bg-muted", platform === "fb" ? "aspect-square" : "aspect-[4/5]")}>
        <img
          src={slideSrc(item, slide.index_n, bust)}
          alt="slajd 1"
          className="h-full w-full object-cover"
        />
      </div>
    );
  }
  if (platform === "fb") {
    return <FbGalleryMosaic item={item} bust={bust} />;
  }
  return <IgGalleryStrip item={item} bust={bust} />;
}

function PreviewCard({
  platform,
  item,
  mode,
  bust,
}: {
  platform: "ig" | "fb";
  item: CarouselItem;
  mode: "reel" | "gallery" | "slide1";
  bust: string | number;
}) {
  const Icon = platform === "ig" ? Instagram : Facebook;
  const label = platform === "ig" ? "Instagram" : "Facebook";
  const corpus = captionCorpus(item);
  const caption = corpus
    ? composeCaptionPreview({
        caption: corpus,
        captionEngage: item.caption_engage || "",
        shortUrl: item.short_url || item.landing_url || "",
        channel: platform,
      })
    : "";
  const czasotekst = isCzasotekst(item);
  const formatHint =
    mode === "reel" ? "Rolka 9:16" : mode === "slide1" ? "Jeden slajd" : "Karuzela / galeria";
  const displayName = platform === "fb" ? "Kasia Zaręba Singer" : "kasiazareba";
  const captionPreview =
    caption && (czasotekst || caption.length <= 220)
      ? caption
      : caption
        ? `${caption.slice(0, 220).trimEnd()}…`
        : "";
  const showMore = Boolean(caption && !czasotekst && caption.length > 220);

  const header = (
    <div className="flex items-start justify-between gap-2 px-3 py-2.5">
      <div className="flex min-w-0 items-center gap-2.5">
        <div className="h-9 w-9 shrink-0 rounded-full bg-gradient-to-br from-rose-300 to-amber-200" />
        <div className="min-w-0">
          <p className="truncate text-[13px] font-semibold leading-tight text-[#050505]">{displayName}</p>
          <p className="mt-0.5 text-[12px] leading-tight text-[#65676b]">
            {platform === "fb" ? (
              <span className="inline-flex items-center gap-1">
                przed chwilą · <Globe className="h-3 w-3" aria-label="publiczny" />
              </span>
            ) : (
              "podgląd przed publikacją"
            )}
          </p>
        </div>
      </div>
      {platform === "fb" ? (
        <MoreHorizontal className="mt-1 h-5 w-5 shrink-0 text-[#65676b]" aria-hidden />
      ) : null}
    </div>
  );

  const captionBlock = (
    <div className="space-y-1 px-3 pb-2.5">
      {captionPreview ? (
        <p className="whitespace-pre-wrap text-[13px] leading-[1.3333] text-[#050505]">
          {platform === "ig" ? <span className="font-semibold">{displayName} </span> : null}
          {captionPreview}
          {platform === "fb" && showMore ? (
            <>
              {" "}
              <span className="font-semibold text-[#050505]">Wyświetl więcej</span>
            </>
          ) : null}
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">Brak caption. Uzupełnij w Wyjściu.</p>
      )}
    </div>
  );

  return (
    <div className="min-w-0 overflow-hidden rounded-lg border border-border bg-white shadow-sm">
      <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
        <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
          {label}
        </span>
        <span className="text-[11px] text-muted-foreground">{formatHint}</span>
      </div>
      {header}
      {platform === "fb" ? (
        <>
          {captionBlock}
          <MediaBlock item={item} mode={mode} bust={bust} platform={platform} />
        </>
      ) : (
        <>
          <MediaBlock item={item} mode={mode} bust={bust} platform={platform} />
          {captionBlock}
        </>
      )}
    </div>
  );
}

export default function PublishPostPreviews({
  item,
  fbPublishMode = "gallery",
}: {
  item: CarouselItem;
  fbPublishMode?: string;
}) {
  const bust = item.updated_at || item.render_done || item.reel_status || "";
  const czasotekst = isCzasotekst(item);
  const fbModeRaw = (fbPublishMode || item.fb_publish_mode || "gallery") as FbMode;
  // IG: tylko czasotekst = rolka; zwykła karuzela zawsze galeria (nawet gdy jest reel FB).
  const igMode: "reel" | "gallery" | "slide1" = czasotekst ? "reel" : "gallery";
  const fbMode: "reel" | "gallery" | "slide1" = czasotekst
    ? "reel"
    : fbModeRaw === "reel"
      ? item.has_reel
        ? "reel"
        : "gallery"
      : fbModeRaw === "slide1"
        ? "slide1"
        : "gallery";

  const hasMedia = Boolean(item.has_reel) || (item.slides || []).length > 0;

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Podgląd postów przed publikacją
      </p>
      {!hasMedia ? (
        <p className="rounded-md border border-dashed border-border bg-muted/10 px-3 py-4 text-sm text-muted-foreground">
          {czasotekst
            ? "Brak złożonej rolki MP4. Najpierw złóż czasotekst w zakładce Wyjście."
            : "Brak złożonych PNG. Najpierw złóż obrazki w zakładce Wyjście."}
        </p>
      ) : (
        <div className={cn("grid gap-3", "lg:grid-cols-2")}>
          <PreviewCard platform="ig" item={item} mode={igMode} bust={bust} />
          <PreviewCard platform="fb" item={item} mode={fbMode} bust={bust} />
        </div>
      )}
    </div>
  );
}
